import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { ToastMessage, ToastType } from './toast.model';

/**
 * Servicio para gestionar notificaciones toast
 */
@Injectable({
  providedIn: 'root',
})
export class ToastService {
  // Los toasts son efímeros: no deben reaparecer al navegar a otra pantalla.
  private toastSubject = new Subject<ToastMessage>();
  private removeToastSubject = new Subject<string>();

  /**
   * Observable de mensajes toast
   */
  public toast$: Observable<ToastMessage> = this.toastSubject.asObservable();

  /**
   * Observable para remover toasts
   */
  public removeToast$: Observable<string> = this.removeToastSubject.asObservable();

  /**
   * Muestra un toast de éxito
   * @param message Mensaje a mostrar
   * @param delay Tiempo en milisegundos antes de ocultarse (default: 5000)
   */
  showSuccess(message: string, delay = 5000): void {
    this.show(message, 'success', delay);
  }

  /**
   * Muestra un toast de error
   * @param message Mensaje a mostrar
   * @param delay Tiempo en milisegundos antes de ocultarse (default: 5000)
   */
  showError(message: string, delay = 5000): void {
    this.show(message, 'error', delay);
  }

  /**
   * Muestra un toast de advertencia
   * @param message Mensaje a mostrar
   * @param delay Tiempo en milisegundos antes de ocultarse (default: 5000)
   */
  showWarning(message: string, delay = 5000): void {
    this.show(message, 'warning', delay);
  }

  /**
   * Muestra un toast de información
   * @param message Mensaje a mostrar
   * @param delay Tiempo en milisegundos antes de ocultarse (default: 5000)
   */
  showInfo(message: string, delay = 5000): void {
    this.show(message, 'info', delay);
  }

  /**
   * Muestra un toast personalizado
   * @param message Mensaje a mostrar
   * @param type Tipo de toast
   * @param delay Tiempo en milisegundos antes de ocultarse (default: 5000)
   * @param autohide Si el toast debe ocultarse automáticamente (default: true)
   */
  show(message: string, type: ToastType = 'info', delay = 5000, autohide = true): void {
    const toast: ToastMessage = {
      id: this.generateId(),
      message,
      type,
      delay,
      autohide,
    };

    // Debug: log para verificar que el servicio emite el toast

    this.toastSubject.next(toast);
  }

  /**
   * Remueve un toast específico
   * @param id ID del toast a remover
   */
  remove(id: string): void {
    this.removeToastSubject.next(id);
  }

  /**
   * Genera un ID único para el toast
   */
  private generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
